<template>
  <UContainer
    :ui="{
      base: 'mx-auto relative',
      padding: 'px-4 sm:px-6 lg:px-8',
      constrained: 'max-w-[1920px]',
    }"
  >
    <ContentPageView v-if="page" :page="page" />

    <!-- Страницу выключили или удалили, пока гость шёл по ссылке -->
    <div v-else-if="isReady" class="content-page-missing">
      <h1 class="text-2xl md:text-3xl font-bold brand-font">Страница не найдена</h1>
      <p class="text-gray-600 dark:text-gray-400">
        Возможно, её убрали. Загляните в меню — там всё на месте.
      </p>
      <UiButtonMain color="red" size="xl" label="Перейти в меню" @click="navigateTo('/')" />
    </div>
  </UContainer>
</template>

<script setup lang="ts">
/**
 * Экран контентной страницы: /p/<slug>.
 *
 * Содержимое лежит в настройках внешнего вида (visual.params_json.appearance.pages),
 * поэтому отдельного запроса не нужно — заведение уже загружено стором.
 * Черновики (published: false) гостям не показываем.
 */
import { computed } from 'vue'
import { useAppearance, useInstitutionStore } from '@entities/institution'
import { ContentPageView } from '@widgets/content-page'

const props = defineProps<{ slug: string }>()

const institutionStore = useInstitutionStore()
const { appearance } = useAppearance()

/** Пока заведение не загрузилось, «не найдено» показывать рано — это мигало бы на каждом заходе. */
const isReady = computed(() => Boolean(institutionStore.institution))

const page = computed(
  () => appearance.value.pages.find((item) => item.slug === props.slug && item.published) ?? null,
)

/**
 * Описание для сниппета — из первого текстового блока страницы.
 *
 * Отдельного поля под description в конструкторе нет, и заводить его ради
 * одной строки не стоит: первый абзац для того и пишется, чтобы объяснить,
 * о чём страница. Обрезаем по границе слова — рваное слово в выдаче читается
 * как ошибка.
 */
const DESCRIPTION_LIMIT = 160

const metaDescription = computed(() => {
  const source = page.value?.blocks?.find(
    (block) => block.type === 'text' && block.text?.trim(),
  )?.text

  const plain = (source || '').replaceAll(/\s+/g, ' ').trim()
  if (!plain) {
    const name = institutionStore.institution?.name
    return name ? `${page.value?.title || 'Страница'} — ${name}.` : ''
  }

  if (plain.length <= DESCRIPTION_LIMIT) return plain
  const cut = plain.slice(0, DESCRIPTION_LIMIT)
  return `${cut.slice(0, cut.lastIndexOf(' ')) || cut}…`
})

const pageTitle = computed(() => {
  const title = page.value?.title?.trim()
  if (!title) return 'Страница не найдена'
  const name = institutionStore.institution?.name?.trim()
  return name ? `${title} — ${name}` : title
})

/**
 * Несуществующий (или снятый с публикации) slug должен отдавать 404.
 *
 * Раньше страница рендерила «Страница не найдена» с кодом 200 — это soft 404:
 * поисковик считает такой адрес рабочей страницей и держит его в индексе,
 * а на сайте копятся «пустые» URL с одинаковым содержимым.
 */
if (import.meta.server) {
  const event = useRequestEvent()
  // Заведение к моменту SSR уже загружено плагином institution-ssr; если нет —
  // судить о существовании страницы рано, и 404 отдавать нельзя.
  if (event && isReady.value && !page.value) setResponseStatus(event, 404)
}

useHead(() => ({
  title: pageTitle.value,
  meta: [
    ...(metaDescription.value ? [{ name: 'description', content: metaDescription.value }] : []),
    // Ненайденную страницу в индекс не пускаем даже при 404: робот мог прийти
    // по старой ссылке, и лишний сигнал тут не помешает.
    ...(page.value ? [] : [{ name: 'robots', content: 'noindex, follow' }]),
  ],
}))
</script>

<style scoped lang="scss">
.content-page-missing {
  @apply max-w-3xl mx-auto py-16 flex flex-col items-start gap-4;
}
</style>
